import { api } from "../../api";

// AWS環境かどうかを判定
const isAWS = process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws";

type Mora = {
  text: string;
  consonant: string;
  consonant_length: number;
  vowel: string;
  vowel_length: number;
  pitch: number;
};
type AccentPhrase = {
  moras: Mora[];
  accent: number;
  pause_mora: Mora;
};

export type Dict = {
  surface: string;
  pronunciation: string;
  accent_type: number;
  priority: number;
};
export type Query = {
  accent_phrases: AccentPhrase[];
  speedScale: number;
  pitchScale: number;
  intonationScale: number;
  volumeScale: number;
  prePhonemeLength: number;
  postPhonemeLength: number;
  outputSamplingRate: number;
  outputStereo: boolean;
  kana: string;
};
export type SpeakerInfo = {
  id: number[];
  // VOICEVOX APIから渡される動的パラメータ群
  query: Partial<Query> & { [key: string]: string | number | boolean | AccentPhrase[] | undefined };
};

/**
 * SpeakerInfoのqueryに定義された値でQueryを上書きする
 *
 * @param query 上書き対象のQueryオブジェクト
 * @param speakerInfo 上書き元のSpeakerInfo
 * @returns 上書き済みのQuery
 */
const EditQuery = (query: Query, speakerInfo: SpeakerInfo) => {
  const source = speakerInfo.query as Partial<Query>;
  const setIfDefined = <K extends keyof Query>(key: K) => {
    const value = source[key];
    if (value !== undefined) {
      query[key] = value as Query[K];
    }
  };
  (Object.keys(query) as (keyof Query)[]).forEach(setIfDefined);
  return query;
};

interface InputObject {
  [key: string]: {
    surface: string;
    pronunciation: string;
    accent_type: number;
    priority: number;
  };
}

export type DictionaryObject = {
  id: string;
  surface: string;
  pronunciation: string;
  accent_type: string;
  priority: string;
};

/**
 * InputObjectをDictionaryObject配列に変換する
 *
 * @param input 変換元のInputObject
 * @returns DictionaryObject配列
 */
export const convertToObjectArray = async (input: InputObject): Promise<DictionaryObject[]> => {
  const output: DictionaryObject[] = [];
  for (const key in input) {
    const item = input[key];
    output.push({
      id: key,
      surface: item.surface,
      pronunciation: item.pronunciation,
      accent_type: item.accent_type.toString(),
      priority: item.priority.toString(),
    });
  }
  return output;
};

/**
 * 2つのDictionaryObject配列が順序を問わず同一かを判定する
 *
 * @param arr1 比較対象の配列1
 * @param arr2 比較対象の配列2
 * @returns 同一ならtrue
 */
export const compareArrays = (arr1: DictionaryObject[], arr2: DictionaryObject[]): boolean => {
  // 配列の長さが異なる場合はすぐに異なると判断
  if (arr1.length !== arr2.length) {
    return false;
  }
  // 配列の順序を無視して、要素が一致しているかどうかを確認するために、
  // ソートされた新しい配列を作成する
  const sortedArr1 = arr1.slice().sort((a, b) => a.id.localeCompare(b.id));
  const sortedArr2 = arr2.slice().sort((a, b) => a.id.localeCompare(b.id));
  // 両方の配列の要素を比較する
  for (let i = 0; i < sortedArr1.length; i++) {
    const item1 = sortedArr1[i];
    const item2 = sortedArr2[i];
    // 要素のプロパティを比較
    if (
      item1.id !== item2.id ||
      item1.surface !== item2.surface ||
      item1.pronunciation !== item2.pronunciation ||
      item1.accent_type !== item2.accent_type ||
      item1.priority !== item2.priority
    ) {
      return false;
    }
  }
  // すべての要素が一致した場合、配列は同じとみなす
  return true;
};

/**
 * VOICEVOX辞書を取得する
 *
 * @returns 辞書データ。失敗時は空オブジェクト
 */
export const getDictionary = async (customerId?: string) => {
  try {
    if (isAWS && customerId) {
      const res = await api.dictionary.getDictionary.query({ customerId });
      return (res as { voicevoxDict?: InputObject }).voicevoxDict ?? {};
    }
    const res = await api.voicevox.getDictionary.query();
    return res;
  } catch (error) {
    console.error("辞書の取得に失敗しました:", error);
    return {};
  }
};

/**
 * VOICEVOX辞書に単語を追加する
 *
 * @param data 追加する単語の辞書データ
 * @returns 追加結果。失敗時はnull
 */
export const addDictionary = async (data: Dict, customerId?: string) => {
  try {
    if (isAWS && customerId) {
      const res = await api.dictionary.addDictionary.mutate({
        customerId,
        surface: data.surface,
        pronunciation: data.pronunciation,
        accentType: data.accent_type,
        priority: data.priority,
      });
      return res;
    }
    const res = await api.voicevox.addDictionary.mutate({
      surface: data.surface,
      pronunciation: data.pronunciation,
      accent_type: data.accent_type,
      priority: data.priority,
    });
    // TODO: デバッグ用コンソールに出力
    // console.info(await getDictionary());
    return res;
  } catch (error) {
    console.error("辞書の追加に失敗しました:", error);
    return null;
  }
};

/**
 * VOICEVOX辞書の単語を更新する
 *
 * @param wordUuid 更新対象の単語UUID
 * @param data 更新する辞書データ
 * @returns 更新結果。失敗時はnull
 */
export const updateDictionary = async (wordUuid: string, data: Dict, customerId?: string) => {
  try {
    if (isAWS && customerId) {
      const res = await api.dictionary.updateDictionary.mutate({
        customerId,
        dictId: wordUuid,
        surface: data.surface,
        pronunciation: data.pronunciation,
        accentType: data.accent_type,
        priority: data.priority,
      });
      return res;
    }
    const res = await api.voicevox.updateDictionary.mutate({
      wordUuid,
      surface: data.surface,
      pronunciation: data.pronunciation,
      accent_type: data.accent_type,
      priority: data.priority,
    });
    // TODO: デバッグ用コンソールに出力
    // console.info(await getDictionary());
    return res;
  } catch (error) {
    console.error("辞書の更新に失敗しました:", error);
    return null;
  }
};

/**
 * VOICEVOX辞書から指定UUIDの単語を削除する
 *
 * @param wordUuid 削除対象の単語UUID
 * @returns 削除結果。失敗時はnull
 */
export const deleteDictionary = async (wordUuid: string, customerId?: string) => {
  try {
    if (isAWS && customerId) {
      const res = await api.dictionary.deleteDictionary.mutate({
        customerId,
        dictId: wordUuid,
      });
      return res;
    }
    const res = await api.voicevox.deleteDictionary.mutate({
      wordUuid: wordUuid,
    });
    return res;
  } catch (error) {
    console.error(`辞書の削除に失敗しました (uuid: ${wordUuid}):`, error);
    return null;
  }
};

/**
 * VOICEVOX辞書の全単語を削除する
 */
export const deleteAllDictionary = async (customerId?: string) => {
  try {
    if (isAWS && customerId) {
      await api.dictionary.deleteAllDictionary.mutate({ customerId });
      return;
    }
    const res = await api.voicevox.getDictionary.query();
    for (const key in res) {
      await deleteDictionary(key);
    }
  } catch (error) {
    console.error("辞書の全削除に失敗しました:", error);
    return;
  }
};

export default EditQuery;
